// src/pages/billing/SuccessPage.jsx
// Success page after Stripe checkout with subscription verification polling

import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

import { billingService } from "../../api/billingService";

const MAX_POLL_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 2000;
const INITIAL_DELAY_MS = 2000;

export default function SuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [countdown, setCountdown] = useState(5);
  const pollAttempts = useRef(0);

  useEffect(() => {
    // Extract session_id from URL query params
    const params = new URLSearchParams(location.search);
    let sessionId = params.get("session_id");
    // Strip surrounding braces — handles old/cached URLs with {cs_test_xxx}
    if (sessionId) {
      sessionId = sessionId.replace(/^\{|\}$/g, "");
    }

    const verifyViaSession = async () => {
      if (!sessionId) return false;
      try {
        const data = await billingService.verifyCheckoutSession(sessionId);
        if (data.access_granted) {
          setStatus("success");
          return true;
        }
        return false;
      } catch (error) {
        console.error("Session verification failed:", error);
        return false;
      }
    };

    const pollStatus = async () => {
      try {
        const data = await billingService.getStatus();
        if (data.access_granted) {
          setStatus("success");
          return true;
        }
        return false;
      } catch (error) {
        console.error("Status poll failed:", error);
        return false;
      }
    };

    const startVerification = async () => {
      // Step 1: Try to activate via verify-checkout-session (immediate)
      const verified = await verifyViaSession();
      if (verified) return;

      // Step 2: Fall back to polling subscription-status
      await new Promise((resolve) => setTimeout(resolve, INITIAL_DELAY_MS));

      const polled = await pollStatus();
      if (polled) return;

      const pollInterval = setInterval(async () => {
        pollAttempts.current += 1;

        const success = await pollStatus();
        if (success) {
          clearInterval(pollInterval);
          return;
        }

        if (pollAttempts.current >= MAX_POLL_ATTEMPTS) {
          clearInterval(pollInterval);
          setStatus("error");
        }
      }, POLL_INTERVAL_MS);

      return () => clearInterval(pollInterval);
    };

    startVerification();
  }, [location.search]);

  // Auto-redirect countdown after success
  useEffect(() => {
    if (status !== "success") return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Check for redirect path saved before payment
          const redirectPath = sessionStorage.getItem("redirectAfterPayment");
          sessionStorage.removeItem("redirectAfterPayment");
          navigate(redirectPath || "/app", { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status, navigate]);

  const handleGoToDashboard = () => {
    const redirectPath = sessionStorage.getItem("redirectAfterPayment");
    sessionStorage.removeItem("redirectAfterPayment");
    navigate(redirectPath || "/app", { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f5f7fa",
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 5,
          maxWidth: 500,
          width: "100%",
          textAlign: "center",
          borderRadius: 3,
        }}
      >
        {status === "verifying" && (
          <>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Processing Your Payment...
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please wait while we confirm your subscription.
            </Typography>
          </>
        )}

        {status === "success" && (
          <>
            {/* Success icon */}
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                bgcolor: "success.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <CheckCircleRoundedIcon sx={{ fontSize: 60, color: "white" }} />
            </Box>

            <Typography variant="h4" fontWeight={800} gutterBottom>
              Welcome to AgentBlayna!
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your subscription is now active. You have full access to all
              features to help you close more deals and never miss a follow-up.
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 3,
              }}
            >
              <BoltRoundedIcon sx={{ color: "primary.main" }} />
              <Typography variant="body2" color="text.secondary">
                Redirecting to dashboard in {countdown} seconds...
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={handleGoToDashboard}
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 700,
                borderRadius: 2,
              }}
            >
              Go to Dashboard Now
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <Alert severity="warning" sx={{ mb: 3 }}>
              We couldn't verify your subscription immediately. Don't worry -
              your payment was processed and you should have access shortly.
            </Alert>

            <Button
              variant="contained"
              size="large"
              onClick={handleGoToDashboard}
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 700,
                borderRadius: 2,
              }}
            >
              Continue to Dashboard
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}
